/**
 * Chapter027.js — Incident Investigation Report (OHSE 027)
 * Migrated from SWMS Platform Chapter027.tsx → JavaScript
 *
 * Props:
 *   data        {array}   incident_investigations from swmsData
 *   onChange    {fn}      (newEntries) => void
 *   companyName {string}  from companyInfo.name
 */

import { useCallback } from 'react';
import { Plus, Trash2 } from 'lucide-react';

const inputCls = 'w-full px-2 py-1.5 text-sm bg-transparent border border-[#e0dbd0] rounded text-[#1a1a2e] placeholder:text-gray-400 focus:outline-none focus:border-[#c9a84c] focus:bg-white transition-all';
const textareaCls = 'w-full px-2 py-1.5 text-sm bg-transparent border border-[#e0dbd0] rounded text-[#1a1a2e] placeholder:text-gray-400 focus:outline-none focus:border-[#c9a84c] focus:bg-white transition-all resize-y min-h-[60px]';

function emptyEntry() {
  return {
    id: crypto.randomUUID(),
    // Class of incident
    injury: false, property_damage: false, near_miss: false, environmental: false, other_class: '',
    reported_details: '', further_action_required: '', report_to_authorities: false,
    // Details of incident
    date_of_incident: '', time_of_incident: '',
    witness_name: '', witness_contact: '',
    nature_of_incident: '', location_of_incident: '',
    description_of_incident: '', details_of_damage: '',
    // Injured person
    injured_person_name: '', injured_person_address: '', injured_person_dob: '',
    injured_person_occupation: '', injured_person_employer: '', referred_to: '',
    // Preventive action
    preventive_action_details: '',
    // Completed by
    completed_by_name: '', completed_by_position: '', completed_by_signature: '', completed_by_date: '',
  };
}

export default function Chapter027({ data = [], onChange, companyName }) {
  const cn = companyName || '[Company Name]';

  const addEntry = useCallback(() => onChange([...data, emptyEntry()]), [data, onChange]);
  const removeEntry = useCallback((id) => onChange(data.filter(e => e.id !== id)), [data, onChange]);
  const updateEntry = useCallback((id, field, value) => {
    onChange(data.map(e => e.id === id ? { ...e, [field]: value } : e));
  }, [data, onChange]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest mb-1">OHSE 027</p>
        <h2 className="text-xl font-bold text-[#1a1a2e] uppercase tracking-wide">Incident Investigation Report</h2>
      </div>

      {/* Declaration */}
      <div className="p-4 bg-[#f8f7f4] rounded-lg border border-[#e0dbd0] text-sm text-[#1a1a2e] leading-relaxed space-y-2">
        <p>
          <span className="font-semibold text-[#c9a84c]">{cn}</span>{' '}
          completes an Incident Investigation Report in the event of any injury involving medical attention
          or off site treatment or in the event of any incidents involving a near miss, property/plant damage
          or injury to the public or the environment.
        </p>
        <p>
          The principal contractor will be informed <span className="font-bold">immediately</span> in the event
          of the above. Following discussions with the principal contractor, a decision will be made as to who
          will conduct the incident investigation. The principal contractor will be provided with a copy of the
          completed Incident Investigation Report.
        </p>
      </div>

      {/* Records */}
      {data.map((entry) => (
        <IncidentCard key={entry.id} entry={entry}
          onUpdate={(field, value) => updateEntry(entry.id, field, value)}
          onRemove={() => removeEntry(entry.id)} />
      ))}

      {data.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center border border-[#e0dbd0] rounded-lg bg-white">
          <p className="text-sm text-gray-400 mb-3">No investigation reports yet.</p>
          <button type="button" onClick={addEntry}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-[#1a1a2e] rounded hover:bg-[#252547] transition-colors">
            <Plus className="w-4 h-4" /> Add Investigation Report
          </button>
        </div>
      )}

      {data.length > 0 && (
        <div className="flex justify-center">
          <button type="button" onClick={addEntry}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[#1a1a2e] rounded hover:bg-[rgba(201,168,76,0.15)] border border-[#c9a84c]/40 transition-colors">
            <Plus className="w-4 h-4" /> Add Investigation Report
          </button>
        </div>
      )}
    </div>
  );
}

function IncidentCard({ entry, onUpdate, onRemove }) {
  const u = (field) => (e) => onUpdate(field, e.target.value);
  const cb = (field) => (e) => onUpdate(field, e.target.checked);

  return (
    <div className="bg-white border border-[#e0dbd0] rounded-lg overflow-hidden">
      <div className="px-4 py-2.5 bg-[#f0ede6] flex items-center justify-between">
        <span className="text-sm font-semibold text-[#1a1a2e] uppercase tracking-wide">
          {entry.date_of_incident
            ? `Incident — ${entry.date_of_incident}`
            : 'Incident Investigation Report'}
        </span>
        <button type="button" onClick={onRemove} className="p-1 text-red-400 hover:text-red-600 transition-colors">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="p-4 space-y-5">
        {/* Class of Incident */}
        <Section title="Class of Incident">
          <div className="flex flex-wrap gap-4 mb-3">
            {[
              ['injury', 'Injury'],
              ['property_damage', 'Property/Plant Damage'],
              ['near_miss', 'Near Miss'],
              ['environmental', 'Environmental'],
            ].map(([field, label]) => (
              <label key={field} className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!entry[field]} onChange={cb(field)}
                  className="w-4 h-4 rounded border-[#c9a84c] accent-[#c9a84c]" />
                <span className="text-sm text-[#1a1a2e]">{label}</span>
              </label>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Other (specify)">
              <input type="text" value={entry.other_class || ''} placeholder="Other incident class"
                onChange={u('other_class')} className={inputCls} />
            </Field>
            <Field label="Details Reported">
              <input type="text" value={entry.reported_details || ''} placeholder="Reported to whom / details"
                onChange={u('reported_details')} className={inputCls} />
            </Field>
            <Field label="Further Action Required">
              <input type="text" value={entry.further_action_required || ''} placeholder="Further actions"
                onChange={u('further_action_required')} className={inputCls} />
            </Field>
            <Field label="">
              <label className="inline-flex items-center gap-2 cursor-pointer mt-5">
                <input type="checkbox" checked={!!entry.report_to_authorities} onChange={cb('report_to_authorities')}
                  className="w-4 h-4 rounded border-[#c9a84c] accent-[#c9a84c]" />
                <span className="text-sm text-[#1a1a2e]">Report to Authorities</span>
              </label>
            </Field>
          </div>
        </Section>

        {/* Details of Incident */}
        <Section title="Details of Incident">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Date of Incident"><input type="date" value={entry.date_of_incident || ''} onChange={u('date_of_incident')} className={inputCls} /></Field>
            <Field label="Time of Incident (24HR)"><input type="text" value={entry.time_of_incident || ''} placeholder="e.g. 14:30" onChange={u('time_of_incident')} className={inputCls} /></Field>
            <Field label="Witness Name"><input type="text" value={entry.witness_name || ''} placeholder="Witness name" onChange={u('witness_name')} className={inputCls} /></Field>
            <Field label="Witness Contact"><input type="text" value={entry.witness_contact || ''} placeholder="Phone / email" onChange={u('witness_contact')} className={inputCls} /></Field>
            <Field label="Nature of Incident"><input type="text" value={entry.nature_of_incident || ''} placeholder="What type of incident?" onChange={u('nature_of_incident')} className={inputCls} /></Field>
            <Field label="Location of Incident"><input type="text" value={entry.location_of_incident || ''} placeholder="Where did it happen?" onChange={u('location_of_incident')} className={inputCls} /></Field>
            <Field label="Description of Incident">
              <textarea value={entry.description_of_incident || ''} placeholder="Describe what happened..." onChange={u('description_of_incident')} className={textareaCls} />
            </Field>
            <Field label="Details of Damage to Equipment/Property">
              <textarea value={entry.details_of_damage || ''} placeholder="Describe any damage..." onChange={u('details_of_damage')} className={textareaCls} />
            </Field>
          </div>
        </Section>

        {/* Injured Person */}
        <Section title="Injured Person/s (if applicable)">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Name"><input type="text" value={entry.injured_person_name || ''} placeholder="Injured person's name" onChange={u('injured_person_name')} className={inputCls} /></Field>
            <Field label="Address"><input type="text" value={entry.injured_person_address || ''} placeholder="Address" onChange={u('injured_person_address')} className={inputCls} /></Field>
            <Field label="Date of Birth"><input type="date" value={entry.injured_person_dob || ''} onChange={u('injured_person_dob')} className={inputCls} /></Field>
            <Field label="Occupation"><input type="text" value={entry.injured_person_occupation || ''} placeholder="Occupation" onChange={u('injured_person_occupation')} className={inputCls} /></Field>
            <Field label="Employer"><input type="text" value={entry.injured_person_employer || ''} placeholder="Employer name" onChange={u('injured_person_employer')} className={inputCls} /></Field>
            <Field label="Referred / Transferred to"><input type="text" value={entry.referred_to || ''} placeholder="Hospital / doctor / other" onChange={u('referred_to')} className={inputCls} /></Field>
          </div>
        </Section>

        {/* Preventive Action */}
        <Section title="Recommended Preventive Action">
          <Field label="Details">
            <textarea value={entry.preventive_action_details || ''} placeholder="What actions will prevent recurrence?"
              onChange={u('preventive_action_details')} className={textareaCls} />
          </Field>
        </Section>

        {/* Completed By */}
        <Section title="Completed By">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Name"><input type="text" value={entry.completed_by_name || ''} placeholder="Full name" onChange={u('completed_by_name')} className={inputCls} /></Field>
            <Field label="Position"><input type="text" value={entry.completed_by_position || ''} placeholder="Job title" onChange={u('completed_by_position')} className={inputCls} /></Field>
            <Field label="Signature (type name)"><input type="text" value={entry.completed_by_signature || ''} placeholder="Signature" onChange={u('completed_by_signature')} className={inputCls} /></Field>
            <Field label="Date"><input type="date" value={entry.completed_by_date || ''} onChange={u('completed_by_date')} className={inputCls} /></Field>
          </div>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 pb-1 border-b border-[#e0dbd0]"
        dangerouslySetInnerHTML={{ __html: title }} />
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      {label && <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>}
      {children}
    </div>
  );
}
